import React, { useEffect, useState, useContext } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import configCtx from '../../services/context.service';
import { LifecycleContainerHelpClass } from '@modules/projects/projectDetail/lifeCycle/components/useDragContainerHelper';

// Create context for drag handle props
const DragContext = React.createContext({});

const DragHandle = () => {
  const { listeners, attributes } = useContext(DragContext);
  return (
    <div
      className="projecthome-drag-handle"
      {...listeners}
      {...attributes}
      style={{ cursor: 'grab' }}
    >
      <span className="appkiticon icon-menu-outline" />
    </div>
  );
};

// Custom modifier that uses the helper container for positioning
const restrictToLifecycleContainer = ({ transform }) => {
  const helperContainer = document.querySelector(
    `.${LifecycleContainerHelpClass}`,
  );

  if (helperContainer) {
    // Helper container exists, transform can be used for bounds checking if needed
    // For now, just return the original transform
  }

  return transform;
};

// Replacement for SortableElement using @dnd-kit
export const SortableItem = ({
  value: component,
  showDragIcon,
  isFirstPhaseShow,
  isFirst,
  isLast,
  index,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `item-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <DragContext.Provider value={{ listeners, attributes }}>
      <div
        ref={setNodeRef}
        style={style}
        className={`lifecycle-phase ${isLast ? 'is-last' : ''}`}
        {...attributes}
      >
        {showDragIcon && <DragHandle />}
        {component}
      </div>
    </DragContext.Provider>
  );
};

// Replacement for SortableContainer using @dnd-kit
export const SortableList = ({ items, disabled, isFirstPhaseShow }) => (
  <div className="lifecycle-phases-container">
    {items.map((component, index) => (
      <SortableItem
        key={`item-${index}`}
        index={index}
        value={component}
        disabled={disabled}
        showDragIcon={!disabled}
        isFirst={index === 0}
        isLast={index === items.length - 1}
      />
    ))}
  </div>
);

function PhaseContainer({ children }) {
  const [items, setItems] = useState(children);
  const [activeId, setActiveId] = useState(null);
  const { isAllEditing, isFirstPhaseShow, handleMovePhaseEnd } =
    useContext(configCtx);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor), // Fixed: removed extra space
  );

  useEffect(() => {
    setItems(children);
  }, [children]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);

      if (!isNaN(oldIndex) && !isNaN(newIndex)) {
        setItems(arrayMove(items, oldIndex, newIndex));
        handleMovePhaseEnd(oldIndex, newIndex);
      }
    }
  };

  const itemIds = items.map((_, index) => `item-${index}`);

  // Get the active item for drag overlay
  const activeIndex = activeId ? parseInt(activeId.split('-')[1]) : -1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToHorizontalAxis, restrictToLifecycleContainer]}
    >
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        <SortableList
          items={items}
          disabled={!isAllEditing}
          isFirstPhaseShow={isFirstPhaseShow}
        />
      </SortableContext>

      {/* DragOverlay provides the dragging element with proper styling */}
      <DragOverlay
        dropAnimation={{
          duration: 300,
          easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
        }}
      >
        {activeItem ? (
          <div className="lifecycle-phase">
            {activeItem} {/* Fixed: removed extra space */}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default PhaseContainer;
